import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listTraits, mapApiTraitToUi } from '../../../services/traits.js';
import { normalizePagedMeta } from '../../../shared/utils/apiResponse.js';
import { apiErrorStatus } from './apiErrorStatus.js';

const EMPTY_META = Object.freeze({
  totalItems: 0,
  totalPages: 0,
  currentPage: 1,
  perPage: 0,
  hasNext: false,
  hasPrevious: false,
  nextCursor: null,
});

const EMPTY_UNVERIFIED = Object.freeze({ count: 0, items: [] });

/**
 * The Traits of one record for the selected chip, filters, search and sort
 * (CT-5715). Same shape as `useActivityFeed`: a race guard, serialized keys for
 * the object inputs, a memoized return. The server narrows; nothing here does.
 *
 * Besides the rows it surfaces what the page needs to pick its state: the
 * unverified group, the sources that are not connected, and whether the viewer
 * is restricted (a 200 with nothing they may see, not a 403).
 */
export function useTraits({
  entityType,
  entityId,
  quickFilter = 'all',
  page = 1,
  pageSize = 10,
  orderBy,
  orderDirection,
  filters,
  search,
} = {}) {
  const [items, setItems] = useState([]);
  const [unverified, setUnverified] = useState(EMPTY_UNVERIFIED);
  const [disconnectedSources, setDisconnectedSources] = useState([]);
  const [restricted, setRestricted] = useState(false);
  const [meta, setMeta] = useState(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  // The VALUES drive a refetch, not the identity of an object literal.
  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(async () => {
    if (!entityType || !entityId) return;
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const parsedSearch = JSON.parse(searchKey);
      const { body, meta: rawMeta } = await listTraits({
        entityType,
        entityId,
        quickFilter,
        page,
        pageSize,
        orderBy,
        orderDirection,
        filters: JSON.parse(filtersKey),
        ...(parsedSearch ? { search: parsedSearch } : {}),
      });
      if (requestId !== fetchIdRef.current) return;
      setItems((body?.items ?? []).map(mapApiTraitToUi));
      setUnverified({
        count: body?.unverified?.count ?? 0,
        items: (body?.unverified?.items ?? []).map(mapApiTraitToUi),
      });
      setDisconnectedSources(
        Object.entries(body?.source_status ?? body?.sourceStatus ?? {})
          .filter(([, value]) => value === 'disconnected')
          .map(([source]) => source),
      );
      setRestricted(Boolean(body?.restricted));
      setMeta(normalizePagedMeta(rawMeta) ?? EMPTY_META);
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setItems([]);
      setUnverified(EMPTY_UNVERIFIED);
      setDisconnectedSources([]);
      setRestricted(false);
      setMeta(EMPTY_META);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load the traits');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId, quickFilter, page, pageSize, orderBy, orderDirection, filtersKey, searchKey]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      items,
      unverified,
      disconnectedSources,
      restricted,
      meta,
      loading,
      error,
      accessDenied: status === 403,
      refetch: fetchData,
    }),
    [items, unverified, disconnectedSources, restricted, meta, loading, error, status, fetchData],
  );
}

export default useTraits;
